import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import AuthContext from 'context/AuthContext'
import { FiBarChart2, FiClock, FiDollarSign, FiUsers, FiCalendar, FiDownload } from 'react-icons/fi'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import Select from 'react-select'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import useToastContext from 'hooks/useToastContext'
import dayjs from 'dayjs'

// Sub-report components
import ScheduledVsActualReport from './ScheduledVsActualReport'
import LaborCostReport from './LaborCostReport'
import AttendancePunctualityReport from './AttendancePunctualityReport'
import OvertimeReport from './OvertimeReport'
import EmployeeHoursSummaryReport from './EmployeeHoursSummaryReport'

const REPORT_TABS = [
  {
    key: 'scheduled_vs_actual',
    label: 'Scheduled vs Actual',
    icon: FiCalendar,
    description: 'Compare scheduled shifts against actual hours worked'
  },
  {
    key: 'labor_cost',
    label: 'Labor Cost',
    icon: FiDollarSign,
    description: 'Monitor payroll expenses by employee and period'
  },
  {
    key: 'attendance_punctuality',
    label: 'Attendance & Punctuality',
    icon: FiClock,
    description: 'Late arrivals, absences, and on-time rates'
  },
  {
    key: 'overtime',
    label: 'Overtime Analysis',
    icon: FiBarChart2,
    description: 'Overtime hours, cost impact, and frequency by employee'
  },
  {
    key: 'employee_hours',
    label: 'Employee Hours',
    icon: FiUsers,
    description: 'Total regular hours worked per employee per period'
  }
]

const ReportsModule = () => {
  const { updatePageName } = useContext(AuthContext)
  const { t } = useTranslation()
  const notification = useToastContext()

  const [activeTab, setActiveTab] = useState('scheduled_vs_actual')
  const [employeeOptions, setEmployeeOptions] = useState([])
  const [selectedEmployees, setSelectedEmployees] = useState([])
  
  // State to hold the export function from the child component
  const [childExportFn, setChildExportFn] = useState(null)
  const [isExporting, setIsExporting] = useState(false)

  const [dateRange, setDateRange] = useState({ 
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'), 
    endDate: dayjs().endOf('month').format('YYYY-MM-DD') 
  })
  
  const [isReset, setIsReset] = useState(false)

  useEffect(() => {
    updatePageName(t('Reports'))
    fetchEmployeeOptions()
  }, [])

  const fetchEmployeeOptions = async () => {
    try {
      const result = await apiGet(apiPath.aremployeeListHR)
      if (result?.data?.success) {
        const options = result.data.results.map(item => ({
          label: `${item.name} (${item.employeeId})`,
          value: item._id
        }))
        setEmployeeOptions(options)
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
    }
  }

  const handleDateChange = (start, end) => {
    setIsReset(false)
    setDateRange({ startDate: start, endDate: end })
  }

  const handleReset = () => {
    setSelectedEmployees([])
    setDateRange({ 
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'), 
      endDate: dayjs().endOf('month').format('YYYY-MM-DD') 
    })
    setIsReset(true)
  }

  // ═══ FIXED: The callback should NOT call the function ═══
  const handleOnExportReady = useCallback((fn) => {
    setChildExportFn(() => fn); // Store as a function, don't call it
  }, []);

  // ═══ HANDLE EXPORT WITH PREVENTION ═══
  const handleTopExport = useCallback(() => {
    // Prevent multiple concurrent exports
    if (isExporting) {
      return;
    }
    
    if (childExportFn) {
      setIsExporting(true);
      // Wrap the child export function
      const wrappedExport = async () => {
        try {
          await childExportFn();
        } catch (error) {
          console.error('🏠 Export failed:', error);
          notification.error('Export failed');
        } finally {
          setIsExporting(false);
        }
      };
      wrappedExport();
    } else {
      notification.error('No data available to export');
    }
  }, [childExportFn, isExporting, notification]);

  const sharedProps = {
    selectedEmployees,
    dateRange,
    employeeOptions,
    onExportReady: handleOnExportReady // Pass the callback, NOT setChildExportFn directly
  }

  const renderActiveReport = () => {
    switch (activeTab) {
      case 'scheduled_vs_actual':
        return <ScheduledVsActualReport {...sharedProps} />
      case 'labor_cost':
        return <LaborCostReport {...sharedProps} />
      case 'attendance_punctuality':
        return <AttendancePunctualityReport {...sharedProps} />
      case 'overtime':
        return <OvertimeReport {...sharedProps} />
      case 'employee_hours':
        return <EmployeeHoursSummaryReport {...sharedProps} />
      default:
        return null
    }
  }

  return (
    <div className='bg-[#F9F9F9] min-h-screen'>
      <div className='px-3 py-4'>
        {/* Report Type Tabs */}
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4'>
          {REPORT_TABS.map(tab => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => {handleReset(); setActiveTab(tab.key);}}
                className={`flex flex-col items-start p-3 rounded-lg border text-left transition-all ${
                  isActive
                    ? 'bg-[#2563EB] border-[#2563EB] text-white shadow-md'
                    : 'bg-white border-[#E9EDF9] text-gray-600 hover:border-[#2563EB] hover:shadow-sm'
                }`}
              >
                <Icon className={`mb-1 text-lg ${isActive ? 'text-white' : 'text-[#2563EB]'}`} />
                <span className='text-xs font-semibold leading-tight'>{tab.label}</span>
              </button>
            )
          })}
        </div>

        {/* Main Report Panel */}
        <div className='bg-white border border-[#E9EDF9] rounded-lg'>
          <div className='border-b border-[#E3E3E3] px-4 py-3'>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <Select
                isMulti
                options={employeeOptions}
                value={selectedEmployees}
                onChange={setSelectedEmployees}
                placeholder='All employees'
                styles={{
                  control: base => ({
                    ...base,
                    minWidth: '220px',
                    borderRadius: '0.5rem',
                    border: '2px solid #DFDFDF',
                    fontSize: '0.875rem'
                  })
                }}
                classNamePrefix='select'
              />
              <div className='flex items-center gap-2 mt-3'>
                <ODateRangePicker
                  handleDateChange={handleDateChange}
                  isReset={isReset}
                  setIsReset={() => setIsReset(false)}
                />
              </div>
              <button
                type='button'
                onClick={handleReset}
                className='px-4 py-2 rounded-lg border border-[#DFDFDF] text-sm text-gray-600 hover:bg-gray-50 transition-colors'
              >
                Reset
              </button>
              
              {/* ═══ TOP EXPORT BUTTON ═══ */}
              <div className='ml-auto'>
                <button
                  type='button'
                  onClick={handleTopExport}
                  disabled={isExporting}
                  className={`flex items-center text-sm px-6 py-2 rounded-lg text-white transition-colors whitespace-nowrap ${
                    isExporting 
                      ? 'bg-blue-300 cursor-not-allowed' 
                      : 'bg-[#2563EB] hover:bg-blue-700'
                  }`}
                >
                  <FiDownload className='mr-2' />
                  {isExporting ? 'Exporting...' : 'Export to Excel'}
                </button>
              </div>
            </div>
          </div>

          <div className='px-4 py-2 bg-[#F9F9F9] border-b border-[#E3E3E3]'>
            <p className='text-sm text-gray-500'>
              {REPORT_TABS.find(t => t.key === activeTab)?.description}
            </p>
          </div>

          <div className='p-4'>
            {renderActiveReport()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportsModule